import axios, { AxiosInstance } from 'axios';

class SunboundPayProxy {

    _axios: AxiosInstance;

    init(baseURL: string) {

        this._axios = axios.create({
            baseURL,
            withCredentials: true
        });
    }

    async login(email: string, password: string) {
        const response = await this._axios.post(`/login`, {
            email, password
        });
        return response.data;
    }

    async logout() {
        const response = await this._axios.post(`/logout`, {});
        return response.data;
    }

    async getSettings() {
        const response = await this._axios.get(`/settings`);
        return response.data;
    }

    async updatePassword(password: string) {
        const response = await this._axios.patch(`/payor/password`, {
            password
        });
        return response.data;
    }

    async resetPassword(password: string, sessionId) {
        const response = await this._axios.patch(`/payor/reset-password`, {
            password
        }, {
            headers: {
                sessionId
            }
        });
        return response.data;
    }

    async resetPasswordCheck(sessionId) {
        const response = await this._axios.get(`/payor/reset-password`, {
            headers: {
                sessionId
            }
        });
        return response.data;
    }

    async pay(amount: string, idInvitation: number, idempotencyKey) {
        const response = await this._axios.post(`/invitations/${idInvitation}/payments`, {
            amount
        }, {
            headers: {
                idempotencyKey
            }
        });
        return response.data;
    }

    async getPayorPayment() {
        const response = await this._axios.get(`/payor/payment`);
        return response.data;
    }

    async addPaymentMethod(idAccount, token, isDefault) {
        const response = await this._axios.post(`/accounts/${idAccount}/payor/payment-methods`, {
            token,
            isDefault
        });
        return response.data;
    }

    async getSession(sessionId) {
        const response = await this._axios.get(`/session`, {
            headers: {
                sessionId
            }
        });
        return response.data;
    }

    async refreshSession() {
        const response = await this._axios.get(`/validate-session`);
        return response.data;
    }

    async getGuarantorTasks(idAccount) {
        const response = await this._axios.get(`/guarantor/accounts/${idAccount}/tasks`);
        return response.data;
    }

    async updateAccountPayorPreferences(idAccount, preferences) {
        const response = await this._axios.patch(`/accounts/${idAccount}/payor/account-preferences`, preferences);
        return response.data;
    }

    async updatePayorPreferences(idPreference, value) {
        const response = await this._axios.put(`/payor/preferences`, {
            idPreference,
            value
        });
        return response.data;
    }

    async setSubscription(idAccount, idProductType, active) {
        const response = await this._axios.put(`/guarantor/accounts/${idAccount}/subscriptions`, {
            idProductType,
            active
        });
        return response.data;
    }

    async disablePayor(idAccount, idPayor) {
        const response = await this._axios.patch(`/guarantor/accounts/${idAccount}/payors/${idPayor}/disable`, {
            isActive: false
        });
        return response.data;
    }

    async enablePayor(idAccount, idPayor) {
        const response = await this._axios.patch(`/guarantor/accounts/${idAccount}/payors/${idPayor}/enable`, {
            isActive: true
        });
        return response.data;
    }

    async getAccountPayors(idAccount) {
        const response = await this._axios.get(`/guarantor/accounts/${idAccount}/payors`);
        return response.data;
    }


    async getEnrolledPayors() {
        const response = await this._axios.get(`/account/enrolled-payors`);
        return response.data;
    }

    async getCurrentInvitation() {
        const response = await this._axios.get(`/payor/current-invitation`);
        return response.data;
    }

    async getPayorTasks(idAccount) {
        const response = await this._axios.get(`/accounts/${idAccount}/payor/tasks`);
        return response.data;
    }

    async getPayorCheck(email: string) {
        const response = await this._axios.get(`/payor-check`, {
            params: {
                email
            }
        });
        return response.data;
    }

    async createPayor(idAccount, newPayor) {
        const response = await this._axios.post(`/guarantor/account/${idAccount}/payors`, {
            ...newPayor
        });
        return response.data;
    }

    async updateEnrollment(idAccount, idInvoice, idProductType, enrollment) {
        const response = await this._axios.patch(`/guarantor/accounts/${idAccount}/invoices/${idInvoice}/enroll`, {
            idProductType,
            enrollment
        });
        return response.data;
    }

    async enrollInvoice(idAccount, idInvoice, idProductType, enrollment) {
        const response = await this._axios.post(`/guarantor/accounts/${idAccount}/invoices/${idInvoice}/enroll`, {
            enrollment,
            idProductType
        });
        return response.data;
    }

    async qualificationInfo(info) {
        const response = await this._axios.post(`/payor/qualifications`, {
            ...info
        });
        return response.data;
    }

    async upsertAccountDefaultPaymentMethod(idAccount, idPaymentMethod) {
        const response = await this._axios.patch(`/accounts/${idAccount}/payor/payment-methods`, {
            defaultPaymentMethod: idPaymentMethod
        });
        return response.data;
    }

    async getPayorPaymentMethods(query) {
        const response = await this._axios.get(`/payor/payment-methods`, { params: query });
        return response.data;
    }

    async getPaymentSettings(idAccount) {
        const response = await this._axios.get(`/accounts/${idAccount}/payor/payment-settings`);
        return response.data;
    }

    async getPayments(idAccount) {
        const response = await this._axios.get(`/accounts/${idAccount}/payor/payments`);
        return response.data;
    }

    async sendPasswordReset(email: string) {
        const response = await this._axios.post(`/payor/send-password-reset`, {
            email
        });
        return response.data;
    }

    async resendWelcome(email: string) {
        const response = await this._axios.post(`/payor/resend-welcome`, {
            email
        });
        return response.data;
    }

    async downloadInvoice(idAccount, idInvoice) {
        const response = await this._axios.get(`/accounts/${idAccount}/payor/invoices/${idInvoice}/document`, { responseType: `blob` });
        return { file: response.data, fileName: response.headers[`x-suggested-filename`] };
    }
}

const sunboundPayProxy = new SunboundPayProxy();

export default sunboundPayProxy;
